import html from "nanohtml";
import type { DetailedPost, Event } from "../../types.ts";
import { ASPECT_RATIOS } from "../../utils/imageUtils.ts";
import { A11Y } from "../../utils/constants.ts";
import { ResponsiveImage } from "./responsiveImage.ts";
import { Link } from "./link.ts";

interface GoDeeperArticleProps extends DetailedPost {
  logger: (event: Event) => void;
  adUnit?: HTMLElement;
}

export function GoDeeperArticle(props: GoDeeperArticleProps): HTMLElement {
  return html`
    <article
      class="rw-mb-8 rw-mt-5 rw-pt-5 rw-border-0 rw-border-t rw-border-solid rw-border-gray-800"
    >
      ${props.authorImage
        ? html`
            <div class="rw-flex rw-gap-1 rw-items-center rw-mb-2">
              <div
                class="rw-w-9 rw-h-9 rw-rounded-full rw-overflow-hidden rw-flex-shrink-0"
              >
                ${props.authorImage.path &&
                !props.authorImage.url.startsWith("data:")
                  ? ResponsiveImage({
                      path: props.authorImage.path,
                      fallbackUrl: props.authorImage.url,
                      aspectRatio: ASPECT_RATIOS.square,
                      alt: `${props.author} profile picture`,
                      className: "rw-w-full rw-h-full rw-object-cover",
                      size: "small",
                    })
                  : html`
                      <img
                        src="${props.authorImage.url}"
                        alt="${props.author} profile picture"
                        class="rw-w-full rw-h-full rw-object-cover"
                        loading="lazy"
                      />
                    `}
              </div>
              <p class="rw-font-bold rw-text-sm rw-text-gray-800 rw-m-0">
                ${props.author}
              </p>
            </div>
          `
        : ""}
      ${Link(
        {
          href: props.url,
          className: "rw-group rw-focus:outline-none",
          logger: props.logger,
          articleId: props.id,
        },
        html`
          <h3
            class="rw-text-xl rw-font-bold rw-leading-tight rw-text-gray-800 rw-mb-4 group-hover:rw-text-blue rw-transition-colors rw-duration-200 md:rw-text-4xl md:rw-leading-tight"
          >
            ${props.title}
          </h3>
        `
      )}
      ${props.description
        ? html`
            <p
              class="rw-text-base rw-text-subtle rw-leading-6 rw-font-bold rw-mt-2.5 rw-mb-4 md:rw-text-lg md:rw-leading-7"
            >
              ${props.description}
            </p>
          `
        : ""}

      <div class="rw-relative rw-mb-4">
        ${props.coverImage.path && !props.coverImage.url.startsWith("data:")
          ? ResponsiveImage({
              path: props.coverImage.path,
              fallbackUrl: props.coverImage.url,
              aspectRatio: ASPECT_RATIOS.widescreen,
              alt: props.title,
              className: "rw-w-full rw-h-auto rw-object-cover rw-rounded",
              size: "large",
            })
          : html`
              <div
                class="rw-w-full rw-overflow-hidden rw-rounded"
                style="aspect-ratio: 16 / 9;"
              >
                <img
                  src="${props.coverImage.url}"
                  alt="${props.title}"
                  class="rw-w-full rw-h-full rw-object-cover"
                  loading="lazy"
                />
              </div>
            `}
        ${props.coverImage.caption
          ? html`
              <div
                class="rw-absolute rw-bottom-0 rw-left-0 rw-right-0 rw-text-white rw-text-xs rw-px-3 rw-py-2 rw-caption-gradient"
              >
                ${props.coverImage.caption}
              </div>
            `
          : ""}
      </div>

      ${props.body
        ? html`
            <div class="rw-text-lg rw-text-gray-800 rw-mb-3 rw-leading-7">
              ${props.body
                .split("\n\n")
                .slice(0, 2)
                .map((paragraph, index) => {
                  const paragraphElement = html`<p class="rw-mb-4">
                    ${paragraph}
                  </p>`;

                  // Inject ad after first paragraph
                  if (index === 0 && props.adUnit) {
                    return html`${paragraphElement}${props.adUnit}`;
                  }

                  return paragraphElement;
                })}
            </div>
          `
        : ""}
      ${Link(
        {
          href: props.url,
          className:
            "rw-bg-primary rw-rounded-lg rw-py-3 rw-px-4 rw-inline-flex rw-items-center rw-gap-1 rw-hover:rw-opacity-80 rw-transition-opacity rw-duration-200",
          logger: props.logger,
          articleId: props.id,
        },
        html`
            <span class="rw-text-lg rw-font-bold rw-text-white">Read More</span>
            <div
              class="rw-flex rw-items-center rw-justify-center rw-w-6 rw-h-6"
            >
              <svg
                width="18"
                height="19"
                viewBox="0 0 18 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect y="0.556152" width="18" height="18" rx="9" fill="white" />
                <path
                  d="M7.2 13.5562L6 12.5562L9.6 9.55615L6 6.55615L7.2 5.55615L12 9.55615L7.2 13.5562Z"
                  fill="#00304D"
                />
              </svg>
            </div>
          </div>
        `
      )}

      <span class="${A11Y.SCREEN_READER_ONLY}">
        Article by ${props.author}, published on
        ${new Date(props.updatedAt).toLocaleDateString()}
      </span>
    </article>
  `;
}
